import { useEffect, useState } from "react";
import { signInWebsiteDemo, signInWebsiteGoogle } from "lib/siteGoogleAuth";
import { Link } from "react-router-dom";
import { safeReturn, siteRequest } from "lib/siteCustomerApi";
import { Notice, Receipt, useOperation, date } from "./Shared";
export function EmailRequest({ purpose = "login", onDone }) {
  const [email, setEmail] = useState(""),
    [marketing, setMarketing] = useState(false),
    [cooldown, setCooldown] = useState(0);
  const op = useOperation();
  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(
      () => setCooldown((v) => Math.max(0, v - 1)),
      1000,
    );
    return () => clearTimeout(timer);
  }, [cooldown]);
  return (
    <form
      className="ca-form"
      onSubmit={async (e) => {
        e.preventDefault();
        const body =
          purpose === "login"
            ? {
                email,
                marketingConsent: marketing,
                returnPath: safeReturn(
                  new URLSearchParams(window.location.search).get("returnPath"),
                ),
                attribution: {
                  sourcePage: "/customer/access",
                  consentVersion: "access-v1",
                },
              }
            : { email };
        const result = await op.run(
          purpose === "login" ? "/auth/request" : "/customer/email-change",
          body,
        );
        if (result) {
          setCooldown(60);
          onDone?.();
        }
      }}
    >
      <label>
        {purpose === "login" ? "Email address" : "New email address"}
        <input
          type="email"
          autoComplete="email"
          maxLength={254}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      {purpose === "login" && (
        <label className="ca-check">
          <input
            type="checkbox"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
          />
          Send me occasional Magellan updates. Optional; you can change this in
          your profile.
        </label>
      )}
      <Receipt op={op}>
        Your request was accepted. If access is available, check your inbox and
        spam folder for a link. Delivery may take a few minutes.
      </Receipt>
      <button className="ca-primary" disabled={op.busy || cooldown > 0}>
        {op.busy
          ? "Requesting…"
          : cooldown
            ? `Request another email in ${cooldown}s`
            : op.receipt
              ? "Send another email"
              : purpose === "login"
                ? "Email me a sign-in link"
                : "Send verification email"}
      </button>
      <p className="ca-muted">
        The link expires after 15 minutes and works once. Opening it will ask
        you to confirm before signing in.
      </p>
    </form>
  );
}
// Golden-thread demo accounts (local live-data server only, opened with ?demo=1).
function DemoSignIn({ onSignedIn }) {
  const [email, setEmail] = useState(""), [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false), [error, setError] = useState(null);
  return (
    <form
      className="ca-form ca-demo-signin"
      onSubmit={async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
          await signInWebsiteDemo(email.trim(), password);
          await onSignedIn();
        } catch (err) {
          setError(err);
        } finally {
          setBusy(false);
        }
      }}
    >
      <p className="ca-divider">Demo account</p>
      <label>
        Demo email
        <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </label>
      <label>
        Password
        <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </label>
      <Notice error={error} />
      <button className="ca-primary" disabled={busy}>{busy ? "Signing in…" : "Demo sign-in"}</button>
    </form>
  );
}
export default function SignIn({ proof, clearProof, onSignedIn, profile, compact = false }) {
  const [info, setInfo] = useState(null),
    [error, setError] = useState(null),
    [busy, setBusy] = useState(false),
    [checked, setChecked] = useState(false);
  useEffect(() => {
    setInfo(null);
    setError(null);
    setChecked(false);
    if (!proof) return;
    const abort = new AbortController();
    siteRequest("/auth/inspect", {
      method: "POST",
      body: proof,
      signal: abort.signal,
    })
      .then((r) => {
        setInfo(r.data);
        setChecked(true);
      })
      .catch((e) => {
        if (!abort.signal.aborted) {
          setError(e);
          setChecked(true);
        }
      });
    return () => abort.abort();
  }, [proof]);
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await siteRequest("/auth/complete", {
        method: "POST",
        body: proof,
      });
      clearProof();
      await onSignedIn();
      if (["/resources", "/checkout"].includes(safeReturn(result.data.returnPath)))
        window.location.assign(safeReturn(result.data.returnPath));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="ca-login-grid">
      {!compact && <div>
        <p className="ca-eyebrow">Customer access</p>
        <h1>Your Magellan files, training, and purchases.</h1>
        <p className="ca-lead">
          Free resources, files shared with you, and help when you need it.
        </p>
        <ul className="ca-benefits">
          <li>A secure link sent to your email</li>
          <li>PDF previews and private downloads</li>
          <li>Your profile, access history and support</li>
        </ul>
        <Link to="/customer/access/tms">
          Purchases, subscriptions &amp; training →
        </Link>
      </div>}
      <div className="ca-panel">
        <h2>{proof ? "Confirm your email link" : "Sign in to your account"}</h2>
        {!proof && (
          <>
            <button
              className="ca-google"
              disabled={busy}
              onClick={async () => {
                if (busy) return;
                setBusy(true);
                setError(null);
                try {
                  await signInWebsiteGoogle();
                  await onSignedIn();
                } catch (e) {
                  setError(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <span aria-hidden="true" className="ca-google-mark">
                G
              </span>
              {busy ? "Connecting…" : "Sign in with Google"}
            </button>
            {process.env.REACT_APP_SITE_DEMO_SIGNIN === "true" && new URLSearchParams(window.location.search).get("demo") === "1" && (
              <DemoSignIn onSignedIn={onSignedIn} />
            )}
            <p className="ca-divider">or sign in with an email link</p>
          </>
        )}
        {proof && !checked && <p role="status">Checking your link…</p>}
        <Notice error={error} />
        {info && (
          <>
            <p>
              {info.purpose === "email_change"
                ? "Confirm your new email address"
                : "Continue as"}{" "}
              <strong>{info.email}</strong>.
            </p>
            <p className="ca-muted">Expires {date(info.expiresAt)}.</p>
            {info.purpose === "email_change" && !profile ? (
              <p className="ca-notice">
                Open this email in the browser where you requested the change.
                If that session ended, sign in with your current email, then
                reopen the verification email.
              </p>
            ) : (
              <button className="ca-primary" disabled={busy} onClick={confirm}>
                {busy
                  ? "Confirming…"
                  : info.purpose === "email_change"
                    ? "Confirm email change"
                    : "Confirm sign-in"}
              </button>
            )}
            <button disabled={busy} onClick={clearProof}>
              Use a different email
            </button>
          </>
        )}
        {(!proof || error) && (
          <>
            <p>
              Enter your email to receive a one-time sign-in link. No password
              is needed for website files.
            </p>
            <EmailRequest onDone={proof ? clearProof : undefined} />
            <p className="ca-muted">
              By continuing, you agree to our{" "}
              <Link to="/terms-of-use">Terms</Link> and{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>. Email sign-in
              does not subscribe you to marketing.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
